import * as React from 'react';
import moment from 'moment';
import SectionHomeWrapper from '../common/wrappers/SectionHomeWrapper';
import '../common/wrappers/SectionHomeWrapper.css';
import CloudAccountsService from '../../services/CloudAccountsService';
import CloudAccount from '../../models/CloudAccount';
import { authenticatedUser } from '../../authentication/authModule';
import { UserRole } from '../../models/enums/UserRole';
import './CloudAccountsHome.css';
import CloudAccountRequestModal from './components/CloudAccountRequestModal';
import CloudAccountDeletionModal from './components/CloudAccountDeletionModal';
import ConfirmModal from '../common/modals/ConfirmModal';
import StandardModal from '../common/modals/StandardModal';
import { WorkflowState } from '../../models/enums/WorkflowState';
import Paginator from '../common/tables/Paginator';
import { TextPassage, TextLink, Button, Alert, AlertVariantType, Icon, Table, Toolbar, ToolbarItem, ProgressBar, Searchbar } from '../common';
import { Tooltip } from '../common/components/Tooltip';
import { Select } from '../common/form-controls/Select';
import { Grid } from '../common/layouts/Grid';
import CloudAccountsHomeTableRow from './CloudAccountsHomeTableRow';
import TextAreaField from '../common/form-controls/TextareaField';
import DatePickerField from '../common/form-controls/DatePickerField';

interface CloudAccountsHomeState {
	actionsEnabled: boolean;
	cloudAccounts: CloudAccount[];
	filteredAccounts: CloudAccount[];
	accountsOnPage: CloudAccount[];
	selectedCloudAccount: CloudAccount;
	searchText: string;
	searchDropdownStatus: string;
	requestAccountModalVisible: boolean;
	resetPasswordModalVisible: boolean;
	resetMfaModalVisible: boolean;
	deleteAccountModalVisible: boolean;
	revalidateAccountModalVisible: boolean;
	isLoading: boolean;
	alertVisible: boolean;
	alertText: string;
	alertType: AlertVariantType;
	approveModalVisible: boolean;
	rejectModalVisible: boolean;
	reasonModalVisible: boolean;
	rejectionReason: string;
	availableAccountTypes: string[];
	selectedDate: Date | null;
	cancelAccountModalVisible: boolean;
	myCloudAccounts: CloudAccount[];
}

export default class CloudAccountsHome extends React.Component<{}, CloudAccountsHomeState> {
	cloudAccountsService: CloudAccountsService;

	timeoutId: NodeJS.Timeout;

	constructor(props: any) {
		super(props);

		this.cloudAccountsService = new CloudAccountsService();
		this.timeoutId = undefined;
		this.state = {
			cloudAccounts: [],
			filteredAccounts: [],
			accountsOnPage: [],
			selectedCloudAccount: undefined,
			searchText: '',
			searchDropdownStatus: 'all',
			requestAccountModalVisible: false,
			deleteAccountModalVisible: false,
			resetPasswordModalVisible: false,
			revalidateAccountModalVisible: false,
			resetMfaModalVisible: false,
			isLoading: true,
			alertVisible: false,
			alertText: '',
			alertType: 'default',
			approveModalVisible: false,
			rejectModalVisible: false,
			reasonModalVisible: false,
			rejectionReason: '',
			availableAccountTypes: [],
			actionsEnabled: true,
			selectedDate: null,
			cancelAccountModalVisible: false,
			myCloudAccounts: [],
		};
	}

	async componentDidMount() {
		this.setState({
			searchDropdownStatus: authenticatedUser.isInRole(UserRole.CloudAccountAdmin) ? 'Submitted' : 'All',
		});

		try {
			await this.updateState();
		} catch (error) {
			const HTTP_INTERNAL_SERVER_ERROR = 500;
			if (error.status === HTTP_INTERNAL_SERVER_ERROR) {
				this.handleError(error, 'Error gathering cloud accounts data');
			}
		}
		this.setState({ isLoading: false });
	}

	updateState = async () => {
		let myCloudAccounts = await this.cloudAccountsService.getMyAccounts();

		await Promise.all(
			myCloudAccounts.map(async (cloudAccount) => {
				cloudAccount.canResetMFA = true;
			})
		);

		const myCloudAccountsForApprove = await this.cloudAccountsService.getAccountsForApprove();

		const filteredAccountsForApprove = myCloudAccountsForApprove.filter(
			(accountForApprove) => !myCloudAccounts.some((myAccount) => myAccount.id === accountForApprove.id)
		);

		let cloudAccounts = [...myCloudAccounts, ...filteredAccountsForApprove];

		if (authenticatedUser.isInRole(UserRole.CloudAccountAdmin)) {
			cloudAccounts = await this.cloudAccountsService.getAll();

			await Promise.all(
				cloudAccounts.map(async (cloudAccount) => {
					myCloudAccounts.forEach((Account) => {
						if (Account.id == cloudAccount.id) cloudAccount.canResetMFA = true;
						else;
					});
				})
			);
		}

		cloudAccounts.sort(
			(a, b) =>
				new Date(b.workflowInstance.createdDate).getTime() - new Date(a.workflowInstance.createdDate).getTime()
		);

		const supportedDomains = (await this.cloudAccountsService.getSupportedDomains()).map((type) => type.domainName);
		const myExistingDomains = myCloudAccounts
			.filter(
				(account) =>
					account.workflowInstance.currentWorkflowInstanceState.workflowState.name ===
						WorkflowState.Approved.name ||
					account.workflowInstance.currentWorkflowInstanceState.workflowState.name ===
						WorkflowState.Submitted.name
			)
			.map((account) => account.createdUserPrincipalName.split('@')[1]);
		const availableAccountTypes = supportedDomains.filter((d) => !myExistingDomains.includes(d));
		this.setState(
			{
				cloudAccounts,
				filteredAccounts: cloudAccounts,
				myCloudAccounts,
				availableAccountTypes,
			},
			() => {
				if (this.state.searchText !== '') {
					this.handleSearchTextChange(this.state.searchText);
				}
				if (this.state.searchDropdownStatus !== 'All') {
					this.handleStatusDropdownChange(this.state.searchDropdownStatus);
				}
			}
		);
	};

	handleStatusDropdownChange = (e) => {
		const dropdownValue = e.target?.value ?? e;
		const searchText = this.state.searchText.toLowerCase();

		this.applyFilters(searchText, dropdownValue);
	};

	handleSearchTextChange = (e) => {
		const searchText = e.target?.value.trim() ?? e;
		const dropdownValue = this.state.searchDropdownStatus;

		this.applyFilters(searchText, dropdownValue);
	};

	handleDateChange = (date) => {
		this.setState({ selectedDate: date }, () => {
			this.applyFilters(this.state.searchText, this.state.searchDropdownStatus);
		});
	};

	applyFilters = (searchText, dropdownValue) => {
		const { cloudAccounts, selectedDate } = this.state;
		const normalizedSearchText = searchText.toLowerCase().trim();

		let filteredAccounts = this.state.cloudAccounts.filter((account) => {
			const matchesAccountName = account.createdUserPrincipalName.toLowerCase().includes(normalizedSearchText);

			const workflowStatus =
				account.workflowInstance.currentWorkflowInstanceState.workflowState.name.toLowerCase();
			const matchesLastModifiedBy =
				workflowStatus === 'submitted'
					? account.workflowInstance.createdBy.displayName.toLowerCase().includes(normalizedSearchText)
					: account.workflowInstance.owner.displayName.toLowerCase().includes(normalizedSearchText);

			const matchesApprover = account.approver?.toLowerCase().includes(normalizedSearchText) ?? false;

			return matchesAccountName || matchesApprover || matchesLastModifiedBy;
		});

		if (dropdownValue !== 'All') {
			filteredAccounts = filteredAccounts.filter(
				(account) =>
					account.workflowInstance.currentWorkflowInstanceState.workflowState.name.toLowerCase() ===
					dropdownValue.toLowerCase()
			);
		}

		if (selectedDate) {
			filteredAccounts = filteredAccounts.filter((account) =>
				moment(account.workflowInstance.createdDate).isSame(moment(selectedDate), 'day')
			);
		}
		this.setState((prev) => ({
			filteredAccounts,
			searchText,
			searchDropdownStatus: dropdownValue,
		}));
	};

	handleSubmitAlert = (alertText: string) => {
		this.setState({
			requestAccountModalVisible: false,
			deleteAccountModalVisible: false,
			alertVisible: true,
			alertType: 'default',
			alertText,
			actionsEnabled: true,
		});
	};

	handleSuccessAlert = (alertText: string) => {
		this.setState(
			(_) => ({
				requestAccountModalVisible: false,
				deleteAccountModalVisible: false,
				alertVisible: true,
				alertType: 'success',
				alertText,
				actionsEnabled: true,
			}),
			() => {
				this.handleSetTimeout();
			}
		);
	};

	handleAccountChange = (oldAccount: CloudAccount, newAccount: CloudAccount) => {
		const index = this.state.cloudAccounts.indexOf(oldAccount);
		const accounts = this.state.cloudAccounts;
		accounts[index] = newAccount;

		// update state and filter again to see changes reflected
		this.setState(
			(_) => ({
				cloudAccounts: accounts,
			}),
			() => {
				this.applyFilters(this.state.searchText, this.state.searchDropdownStatus);
			}
		);
	};

	handlePasswordResetRequest = async () => {
		this.setState({ resetPasswordModalVisible: false });

		try {
			const cloudAccount = this.state.selectedCloudAccount;

			this.handleSubmitAlert('Submitting request...');

			await this.cloudAccountsService.requestPasswordReset(cloudAccount.createdUserPrincipalName);

			this.handleSuccessAlert(`An email has been sent containing a link to update the password for your account. 
								Please check your junk folder if you don't receive anything within a few minutes.`);
		} catch (error) {
			this.handleError(error);
		}
	};

	handleMfaResetRequest = async () => {
		this.setState({ resetMfaModalVisible: false });

		try {
			const cloudAccount = this.state.selectedCloudAccount;

			this.handleSubmitAlert('Submitting request...');

			await this.cloudAccountsService.resetMfaConfiguration(
				cloudAccount.createdUserPrincipalName,
				authenticatedUser.userEmail
			);

			this.handleSuccessAlert(`The MFA configuration has been reset.`);
		} catch (error) {
			this.handleError(error);
		}
	};

	handleRequestApproved = async () => {
		this.setState({ approveModalVisible: false });

		try {
			const cloudAccount = this.state.selectedCloudAccount;

			this.handleSubmitAlert('Submitting request...');

			const updatedAccount = cloudAccount.approver
				? await this.cloudAccountsService.approveUserCloudAccount(cloudAccount.id)
				: await this.cloudAccountsService.approveCloudAccount(cloudAccount.id);

			this.handleAccountChange(cloudAccount, updatedAccount);

			this.handleSuccessAlert(
				`The cloud-only account request '${cloudAccount.createdUserPrincipalName}' has been approved.`
			);
		} catch (error) {
			this.handleError(error);
		}
	};

	handleRequestRejected = async () => {
		this.setState({ rejectModalVisible: false });

		try {
			const cloudAccount = this.state.selectedCloudAccount;

			this.handleSubmitAlert('Submitting request...');

			const updatedAccount = cloudAccount.approver
				? await this.cloudAccountsService.rejectUserCloudAccount(cloudAccount.id, this.state.rejectionReason)
				: await this.cloudAccountsService.rejectCloudAccount(cloudAccount.id, this.state.rejectionReason);

			this.handleAccountChange(cloudAccount, updatedAccount);

			this.handleSuccessAlert(
				`The cloud-only account request '${cloudAccount.createdUserPrincipalName}' has been rejected.`
			);
		} catch (error) {
			this.handleError(error);
		} finally {
			this.setState({ rejectionReason: '' });
		}
	};

	handleAccountCancelation = async () => {
		this.setState({ cancelAccountModalVisible: false });

		try {
			const cloudAccount = this.state.selectedCloudAccount;

			this.handleSubmitAlert('Submitting request...');

			await this.cloudAccountsService.accountCancelationRequest(cloudAccount.id);

			this.handleSuccessAlert(`The account request has been canceled.`);
		} catch (error) {
			this.handleError(error);
		}
	};

	handleAccountRevalidation = async () => {
		this.setState({ revalidateAccountModalVisible: false });

		try {
			const cloudAccount = this.state.selectedCloudAccount;

			this.handleSubmitAlert('Revalidating account...');

			await this.cloudAccountsService.revalidateCloudAccount(cloudAccount.id);

			this.handleSuccessAlert(`The account request has been revalidated.`);
		} catch (error) {
			this.handleError(error);
		}
	};

	canRevalidateAccount = (status: string, CloudAccount: CloudAccount): boolean => {
		const owner = CloudAccount.workflowInstance.owner;

		return (
			status === 'Error' &&
			(authenticatedUser.isInRole(UserRole.CloudAccountAdmin) || authenticatedUser.userId == owner.id)
		);
	};

	handleSubmit = async (alertText: string) => {
		this.setState({
			requestAccountModalVisible: false,
			deleteAccountModalVisible: false,
			alertVisible: true,
			alertType: 'default',
			alertText,
			actionsEnabled: false,
		});

		this.handleSetTimeout();
	};

	handleSuccess = async (alertText: string) => {
		this.setState({
			resetMfaModalVisible: false,
			requestAccountModalVisible: false,
			deleteAccountModalVisible: false,
			alertVisible: true,
			alertType: 'success',
			alertText,
			actionsEnabled: true,
			isLoading: true,
		});

		this.handleSetTimeout();

		await this.updateState();
		this.setState({ isLoading: false });
	};

	handleError = async (
		error,
		message = 'Your request could not be completed at this time. Please try again later.'
	) => {
		this.setState({
			resetMfaModalVisible: false,
			requestAccountModalVisible: false,
			deleteAccountModalVisible: false,
			alertVisible: true,
			alertType: 'error',
			alertText: error.body?.message ?? message,
			isLoading: true,
			actionsEnabled: true,
		});

		this.handleSetTimeout();
		await this.updateState();
		this.setState({ isLoading: false });

		throw error;
	};

	handleAlertClose = () => {
		this.setState({
			alertVisible: false,
			alertText: '',
			alertType: 'default',
		});
	};

	handleSetTimeout = () => {
		if (this.timeoutId !== undefined) {
			window.clearTimeout(this.timeoutId);
		}

		this.timeoutId = setTimeout(this.handleAlertClose, 10000);
	};

	render() {
		const statusRows = [];
		this.state.selectedCloudAccount?.workflowInstance.workflowInstanceStates.forEach((state) => {
			statusRows.push(
				<TextAreaField
					key={state.workflowInstanceId}
					label={`${state.workflowState.name} on ${moment(state.performedOnDate).format('MM/DD/YYYY')} by ${state.performedByUser.displayName}`}
					disabled
					value={`Reason: ${state.performedReason}`}
				/>
			);
		});

		return (
			<SectionHomeWrapper title="Cloud-only Accounts Management">
				<TextPassage>
					<p>
						For more information, please follow{' '}
						<TextLink
							target="_blank"
							external
							href="https://appwiki.xom.cloud/docs/admin/cloudssp/CloudSelfServicePortal.html?#cloud-only-accounts"
						>
							this link
						</TextLink>
					</p>
				</TextPassage>
				<br />
				{this.state.alertVisible && (
					<Alert
						variant={this.state.alertType}
						onClick={this.handleAlertClose}
						onClose={this.handleAlertClose}
						body={this.state.alertText}
					/>
				)}
				<ConfirmModal
					visible={this.state.approveModalVisible}
					title={`Approve the cloud-only account request '${this.state.selectedCloudAccount?.createdUserPrincipalName}'?`}
					question={`Are you sure you want to approve this request? 
							   A notification will be sent to ${this.state.selectedCloudAccount?.workflowInstance.createdBy.displayName} (${this.state.selectedCloudAccount?.workflowInstance.createdBy.userPrincipalName.address}) 
							   upon confirmation informing them of this change.`}
					confirmButton={{ label: 'Confirm Approval' }}
					onConfirm={this.handleRequestApproved}
					onCancel={() => this.setState({ approveModalVisible: false })}
				/>
				<ConfirmModal
					visible={this.state.rejectModalVisible}
					title={`Reject the cloud-only account request '${this.state.selectedCloudAccount?.createdUserPrincipalName}'?`}
					question={`Are you sure you want to reject this request?
							   A notification will be sent to ${this.state.selectedCloudAccount?.workflowInstance.createdBy.displayName} (${this.state.selectedCloudAccount?.workflowInstance.createdBy.userPrincipalName.address}) 
							   upon confirmation informing them of this change.`}
					confirmButton={{ label: 'Confirm Rejection' }}
					confirmationField={{
						label: 'Please provide a reason for rejecting this request.',
						value: this.state.rejectionReason,
						validation: { required: true },
						onChange: (e) => this.setState({ rejectionReason: e.target.value }),
					}}
					onConfirm={this.handleRequestRejected}
					onCancel={() => {
						this.setState({ rejectModalVisible: false, rejectionReason: '' });
					}}
				/>
				<ConfirmModal
					visible={this.state.resetPasswordModalVisible}
					title={`Reset the password for '${this.state.selectedCloudAccount?.createdUserPrincipalName}'?`}
					question={`An email will be sent to ${this.state.selectedCloudAccount?.workflowInstance.owner.displayName}'s primary ExxonMobil email address with a link to complete the process.`}
					onConfirm={this.handlePasswordResetRequest}
					onCancel={() => this.setState({ resetPasswordModalVisible: false })}
				/>
				<ConfirmModal
					visible={this.state.resetMfaModalVisible}
					title={`Reset MFA configuration for '${this.state.selectedCloudAccount?.createdUserPrincipalName}'?`}
					question={`The MFA configuration for the selected account will be reset.`}
					onConfirm={this.handleMfaResetRequest}
					onCancel={() => this.setState({ resetMfaModalVisible: false })}
				/>
				<ConfirmModal
					visible={this.state.cancelAccountModalVisible}
					title={`Cancel the request to create the account for '${this.state.selectedCloudAccount?.createdUserPrincipalName}'?`}
					question={`The account request for the selected account will be canceled.`}
					onConfirm={this.handleAccountCancelation}
					onCancel={() => this.setState({ cancelAccountModalVisible: false })}
				/>
				<ConfirmModal
					visible={this.state.revalidateAccountModalVisible}
					title={`Revalidate the request to create the account for '${this.state.selectedCloudAccount?.createdUserPrincipalName}'?`}
					question={`The account request for the selected account will be revalidated. This means that if it was approved in the past, it will revert to its original state.`}
					onConfirm={this.handleAccountRevalidation}
					onCancel={() => this.setState({ revalidateAccountModalVisible: false })}
				/>

				<StandardModal
					show={this.state.reasonModalVisible}
					onDismiss={() => {
						this.setState({ reasonModalVisible: false });
					}}
					title={this.state.selectedCloudAccount?.createdUserPrincipalName || 'Request Information'}
					footer={
						<Grid variant="2up">
							<Grid.Item>
								<Button
									variant="secondary"
									onClick={() => {
										this.setState({ reasonModalVisible: false });
									}}
								>
									Close
								</Button>
							</Grid.Item>
						</Grid>
					}
				>
					<div id="reason-modal-body">{statusRows}</div>
				</StandardModal>
				{this.state.isLoading ? (
					<ProgressBar indeterminate label="Loading your accounts..." />
				) : (
					<Table
						header={
							<Toolbar>
								<ToolbarItem width='md'>
									<Searchbar
										onClick={(e) => e.preventDefault()}
										value={this.state.searchText}
										onChange={(e) => this.handleSearchTextChange(e)}
										placeholder={'Search (i.e. Account Name, Approver, Last Modified By)'}
									/>
								</ToolbarItem>
								<ToolbarItem>
									<Select
										className="em-u-margin-bottom-none dropdown-filter-color"
										onChange={this.handleStatusDropdownChange}
										value={this.state.searchDropdownStatus}
										options={[
											'All',
											'Submitted',
											'Approved',
											'Rejected',
											'Cancelled',
											'Deleted',
											'Error',
										]}
									/>
								</ToolbarItem>
								<ToolbarItem className='accounts-custom-datepicker' width='sdate'>
									<DatePickerField
										id="cloud-accounts-datepicker"
										label="Select Date"
										onChange={(e) => this.handleDateChange(e.target.value)}
										placeholder="YYYY-MM-DD"
										value={
											this.state.selectedDate
												? moment(this.state.selectedDate).format('YYYY-MM-DD')
												: ''
										}
									/>
								</ToolbarItem>
								<ToolbarItem right>
									{this.state.availableAccountTypes.length === 0 ? (
										<Tooltip
											content={
												'Our records show you already have an active account for each of the supported account types listed above.'
											}
										>
											<Button disabled variant="primary" size="small">
												<Icon size="small" icon="plus-filled" />
												&nbsp; Request Cloud-only Account
											</Button>
										</Tooltip>
									) : (
										<Button
											variant="primary"
											size="small"
											onClick={() => this.setState({ requestAccountModalVisible: true })}
										>
											<Icon size="small" icon="plus-filled" />
											&nbsp; Request Cloud-only Account
										</Button>
									)}
								</ToolbarItem>
							</Toolbar>
						}
						footer={
							<Paginator
								data={this.state.filteredAccounts}
								onPageChange={(accounts: CloudAccount[]) => {
									this.setState({
										accountsOnPage: accounts,
									});
								}}
							/>
						}
					>
						<Table.Head>
							<Table.Head.Row>
								<Table.Head.Cell>Account Name</Table.Head.Cell>
								<Table.Head.Cell className="em-u-text-align-center">Status</Table.Head.Cell>
								<Table.Head.Cell className="em-u-text-align-center">Approver</Table.Head.Cell>
								<Table.Head.Cell className="em-u-text-align-center">
									{authenticatedUser.isInRole(UserRole.CloudAccountAdmin)
										? 'Requested By'
										: 'Last Modified By'}
								</Table.Head.Cell>
								<Table.Head.Cell className="em-u-text-align-center">Date Requested</Table.Head.Cell>
								<Table.Head.Cell className="em-u-text-align-center">Action</Table.Head.Cell>
							</Table.Head.Row>
						</Table.Head>
						<Table.Body>
							{this.state.accountsOnPage.map((cloudAccount) => (
								<CloudAccountsHomeTableRow
									key={cloudAccount.id}
									cloudAccount={cloudAccount}
									actionsEnabled={this.state.actionsEnabled}
									status={
										cloudAccount.workflowInstance.currentWorkflowInstanceState.workflowState.name
									}
									isMyAccount={cloudAccount.createdUserPrincipalName === authenticatedUser.userEmail}
									setResetPasswordModalVisible={(visible) =>
										this.setState({ resetPasswordModalVisible: visible })
									}
									setSelectedCloudAccount={(cloudAccount) =>
										this.setState({ selectedCloudAccount: cloudAccount })
									}
									setResetMfaModalVisible={(visible) =>
										this.setState({ resetMfaModalVisible: visible })
									}
									setDeleteAccountModalVisible={(visible) =>
										this.setState({ deleteAccountModalVisible: visible })
									}
									setApproveModalVisible={(visible) =>
										this.setState({ approveModalVisible: visible })
									}
									setRejectModalVisible={(visible) => this.setState({ rejectModalVisible: visible })}
									setReasonModalVisible={(visible) => this.setState({ reasonModalVisible: visible })}
									setRevalidateAccountModalVisible={(visible) =>
										this.setState({ revalidateAccountModalVisible: visible })
									}
									setCancelAccountModalVisible={(visible) =>
										this.setState({ cancelAccountModalVisible: visible })
									}
								/>
							))}
						</Table.Body>
					</Table>
				)}
				<CloudAccountRequestModal
					visible={this.state.requestAccountModalVisible}
					availableAccountTypes={this.state.availableAccountTypes}
					onSubmit={this.handleSubmit}
					onSuccess={this.handleSuccess}
					onError={this.handleError}
					onClose={() =>
						this.setState({
							requestAccountModalVisible: false,
						})
					}
				/>
				<CloudAccountDeletionModal
					visible={this.state.deleteAccountModalVisible}
					cloudAccount={this.state.selectedCloudAccount}
					onSubmit={this.handleSubmitAlert}
					onChange={this.handleAccountChange}
					onSuccess={this.handleSuccessAlert}
					onError={this.handleError}
					onClose={() =>
						this.setState({
							deleteAccountModalVisible: false,
						})
					}
				/>
			</SectionHomeWrapper>
		);
	}
}
